import React from 'react';
import { Box, Card, CardContent, Typography, Grid, Chip } from '@mui/material';
import {
  FolderOpen,
  CheckCircle,
  AttachMoney,
  Warning,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { status, border } from '../../theme/colors';

/**
 * Project Summary Dashboard
 * Displays high-level statistics about all projects
 * Cards are clickable to filter projects
 */
const ProjectSummaryDashboard = ({ projects, onFilterChange }) => {
  // Calculate statistics
  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => 
    p.status === 'in_progress' || p.status === 'active'
  ).length;
  const completedProjects = projects.filter(p => p.status === 'completed').length;
  const atRiskProjects = projects.filter(p => {
    // Project is at risk if progress is below expected based on time elapsed
    const progress = parseFloat(p.progress || 0);
    return progress < 50 && p.status === 'in_progress';
  }).length;

  // Calculate total budget
  const totalBudget = projects.reduce((sum, p) => {
    const budget = parseFloat(p.budget || 0);
    return sum + budget;
  }, 0);

  // Calculate budget spent
  const budgetSpent = projects.reduce((sum, p) => {
    const budget = parseFloat(p.budget || 0);
    const progress = parseFloat(p.progress || 0) / 100;
    return sum + (budget * progress);
  }, 0);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const stats = [
    {
      title: 'Total Projects',
      value: totalProjects,
      icon: <FolderOpen sx={{ fontSize: 40, color: status.info }} />,
      color: status.info,
      bgColor: `${status.info}15`,
      subtitle: `${activeProjects} active`,
      trend: null,
      filterAction: () => onFilterChange({ status: 'all', sortBy: 'recent' })
    },
    {
      title: 'Active Projects',
      value: activeProjects,
      icon: <CheckCircle sx={{ fontSize: 40, color: status.success }} />,
      color: status.success,
      bgColor: `${status.success}15`,
      subtitle: `${completedProjects} completed`,
      trend: activeProjects > completedProjects ? 'up' : 'down',
      filterAction: () => onFilterChange({ status: 'in_progress', sortBy: 'recent' })
    },
    {
      title: 'Total Budget',
      value: formatCurrency(totalBudget),
      icon: <AttachMoney sx={{ fontSize: 40, color: status.warning }} />,
      color: status.warning,
      bgColor: `${status.warning}15`,
      subtitle: `${formatCurrency(budgetSpent)} spent`,
      trend: budgetSpent < totalBudget * 0.8 ? 'up' : 'down',
      filterAction: () => onFilterChange({ status: 'all', sortBy: 'budget_high' })
    },
    {
      title: 'At Risk',
      value: atRiskProjects,
      icon: <Warning sx={{ fontSize: 40, color: status.error }} />,
      color: status.error,
      bgColor: `${status.error}15`,
      subtitle: atRiskProjects > 0 ? 'Needs attention' : 'All on track',
      trend: atRiskProjects > 0 ? 'down' : 'up',
      filterAction: () => onFilterChange({ status: 'in_progress', sortBy: 'progress_low' })
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={2}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <Card
              onClick={stat.filterAction}
              sx={{
                height: '100%',
                bgcolor: 'white',
                border: `1px solid ${border.default}`,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: stat.color,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: stat.bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(stat.icon, { sx: { fontSize: 28, color: stat.color } })}
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#666', fontWeight: 500, fontSize: '12px', mb: 0.5 }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1, fontSize: '24px' }}
                    >
                      {stat.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
                        {stat.subtitle}
                      </Typography>
                      {stat.trend && (
                        stat.trend === 'up' ? (
                          <TrendingUp sx={{ fontSize: 12, color: status.success }} />
                        ) : (
                          <TrendingDown sx={{ fontSize: 12, color: status.error }} />
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ProjectSummaryDashboard;
